import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Chip,
  LinearProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tab,
  Tabs,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  TextField,
  Skeleton,
  Alert,
  Paper,
  Divider,
  ListItemButton
} from '@mui/material';
import { useTheme, alpha, styled } from '@mui/material/styles';
import {
  EmojiEvents as TrophyIcon,
  PersonAdd as PersonAddIcon,
  Business as BusinessIcon,
  Campaign as CampaignIcon,
  AttachMoney as MoneyIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Star as StarIcon,
  StarHalf as StarHalfIcon,
  CheckCircle as CheckCircleIcon,
  Rocket as RocketIcon,
  Pause as PauseIcon,
  Edit as EditIcon,
  Archive as ArchiveIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { format, subDays } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import client from "../../API";

// Styled Components
const GlassCard = styled(Card)(({ theme, rank }) => ({
  backdropFilter: 'blur(20px)',
  backgroundColor: alpha(theme.palette.background.paper, 0.9),
  borderRadius: 16,
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  position: 'relative',
  overflow: 'visible',
  '&:hover': {
    transform: 'translateY(-8px)',
    boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.2)}`,
    borderColor: alpha(theme.palette.primary.main, 0.3),
  },
  ...(rank === 1 && {
    borderColor: alpha('#FFD700', 0.5),
    boxShadow: `0 8px 32px ${alpha('#FFD700', 0.2)}`,
    '&:hover': {
      boxShadow: `0 16px 48px ${alpha('#FFD700', 0.3)}`,
    }
  }),
  ...(rank === 2 && {
    borderColor: alpha('#C0C0C0', 0.5),
  }),
  ...(rank === 3 && {
    borderColor: alpha('#CD7F32', 0.5),
  })
}));

const RankBadge = styled(Box)(({ theme, rank }) => {
  let backgroundColor = theme.palette.grey[500];
  let color = theme.palette.common.white;
  
  if (rank === 1) {
    backgroundColor = '#FFD700';
    color = '#000';
  } else if (rank === 2) {
    backgroundColor = '#C0C0C0';
    color = '#000';
  } else if (rank === 3) {
    backgroundColor = '#CD7F32';
    color = '#fff';
  }
  
  return {
    position: 'absolute',
    top: -12,
    right: 16,
    width: 48,
    height: 48,
    borderRadius: '50%',
    backgroundColor,
    color,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 800,
    fontSize: '1.25rem',
    boxShadow: `0 4px 16px ${alpha(backgroundColor, 0.4)}`,
    zIndex: 1,
  };
});

const MetricCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: 12,
  backgroundColor: alpha(theme.palette.background.default, 0.5),
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.background.default, 0.7),
    transform: 'translateY(-2px)',
  }
}));

const ClickableListItem = styled(ListItemButton)(({ theme }) => ({
  borderRadius: 12,
  marginBottom: theme.spacing(1),
  border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    borderColor: alpha(theme.palette.primary.main, 0.3),
    transform: 'translateX(8px)',
  }
}));

// Campaign status configuration
const CAMPAIGN_STATUS_CONFIG = {
  'Completed': {
    priority: 1,
    color: 'success',
    icon: CheckCircleIcon,
    label: 'Completed'
  },
  'Launched': {
    priority: 2,
    color: 'primary',
    icon: RocketIcon,
    label: 'Launched'
  },
  'Paused': {
    priority: 3,
    color: 'warning',
    icon: PauseIcon,
    label: 'Paused'
  },
  'Draft': {
    priority: 4,
    color: 'default',
    icon: EditIcon,
    label: 'Draft'
  },
  'Archived': {
    priority: 5,
    color: 'default',
    icon: ArchiveIcon,
    label: 'Archived'
  }
};

// Creator status configuration (assuming status field contains this info)
const getCreatorStatusConfig = (status) => {
  const statusLower = status?.toLowerCase() || '';
  
  if (statusLower.includes('partner')) {
    return {
      tier: 'partner',
      color: 'primary',
      icon: StarIcon,
      label: 'Partner',
      priority: 1
    };
  } else if (statusLower.includes('associate')) {
    return {
      tier: 'associate',
      color: 'info',
      icon: StarHalfIcon,
      label: 'Associate',
      priority: 2
    };
  }
  
  return {
    tier: 'standard',
    color: 'default',
    icon: null,
    label: status || 'Standard',
    priority: 3
  };
};

const AMTracking = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAM, setSelectedAM] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsData, setDetailsData] = useState(null);
  const [detailsTab, setDetailsTab] = useState(0);
  const [dateRange, setDateRange] = useState({
    start: format(subDays(new Date(), 14), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd')
  });

  const fetchMetrics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await client.amTracking.getMetrics({
        start_date: `${dateRange.start}T00:00:00Z`,
        end_date: `${dateRange.end}T23:59:59Z`
      });
      setMetrics(response.metrics);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to fetch metrics');
      console.error('Error fetching AM metrics:', err);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  const fetchDetails = useCallback(async (userId) => {
    setDetailsLoading(true);
    try {
      const response = await client.amTracking.getDetails(userId, {
        start_date: `${dateRange.start}T00:00:00Z`,
        end_date: `${dateRange.end}T23:59:59Z`
      });
      
      // Sort campaigns by status priority
      if (response.campaigns) {
        response.campaigns.sort((a, b) => {
          const priorityA = CAMPAIGN_STATUS_CONFIG[a.campaign_status]?.priority || 999;
          const priorityB = CAMPAIGN_STATUS_CONFIG[b.campaign_status]?.priority || 999;
          return priorityA - priorityB;
        });
      }
      
      setDetailsData(response);
    } catch (err) {
      console.error('Error fetching AM details:', err);
    } finally {
      setDetailsLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const handleCardClick = (am) => {
    setSelectedAM(am);
    setDetailsOpen(true);
    setDetailsTab(0);
    fetchDetails(am.user_id);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setSelectedAM(null);
    setDetailsData(null);
  };

  const handleNavigate = (path) => {
    handleCloseDetails();
    navigate(path);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const MetricDisplay = ({ icon: Icon, label, value, color = 'primary' }) => (
    <MetricCard elevation={0}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: alpha(theme.palette[color].main, 0.1),
            color: theme.palette[color].main,
          }}
        >
          <Icon fontSize="small" />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            {label}
          </Typography>
          <Typography variant="h6" fontWeight={700}>
            {typeof value === 'number' && value > 999 ? formatCurrency(value) : value}
          </Typography>
        </Box>
      </Box>
    </MetricCard>
  );

  const CampaignStatusChip = ({ status }) => {
    const config = CAMPAIGN_STATUS_CONFIG[status] || CAMPAIGN_STATUS_CONFIG['Draft'];
    const Icon = config.icon;
    
    return (
      <Chip
        size="small"
        icon={<Icon />}
        label={config.label}
        color={config.color}
        sx={{ fontWeight: 600 }}
      />
    );
  };

  const CreatorStatusChip = ({ status }) => {
    const config = getCreatorStatusConfig(status);
    const Icon = config.icon;
    
    return (
      <Chip
        size="small"
        icon={Icon ? <Icon /> : undefined}
        label={config.label}
        color={config.color}
        sx={{ fontWeight: 600 }}
      />
    );
  };

  const LoadingSkeleton = () => (
    <Grid container spacing={3}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Grid item xs={12} sm={6} lg={4} key={i}>
          <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 4 }} />
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <TrophyIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
            <Box>
              <Typography variant="h4" fontWeight={800}>
                Account Manager Leaderboard
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Performance metrics for the last {Math.ceil((new Date(dateRange.end) - new Date(dateRange.start)) / (1000 * 60 * 60 * 24))} days
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={fetchMetrics} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Box>

        {/* Date Range Selector */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            label="Start Date"
            type="date"
            size="small"
            value={dateRange.start}
            onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 200 }}
          />
          <TextField
            label="End Date"
            type="date"
            size="small"
            value={dateRange.end}
            onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
            InputLabelProps={{ shrink: true }}
            sx={{ width: 200 }}
          />
          <Button variant="contained" onClick={fetchMetrics} disabled={loading}>
            Update
          </Button>
        </Box>
      </Box>

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && <LoadingSkeleton />}

      {/* Metrics Grid */}
      {!loading && !error && (
        <Grid container spacing={3}>
          {metrics.map((am, index) => (
            <Grid item xs={12} sm={6} lg={4} key={am.user_id}>
              <GlassCard rank={index + 1} onClick={() => handleCardClick(am)}>
                <RankBadge rank={index + 1}>
                  {index + 1}
                </RankBadge>
                
                <CardContent sx={{ pt: 4 }}>
                  {/* User Info */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                    <Avatar
                      src={am.user_photo}
                      sx={{ width: 64, height: 64, border: `3px solid ${alpha(theme.palette.primary.main, 0.2)}` }}
                    >
                      {am.user_name.charAt(0)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="h6" fontWeight={700} noWrap>
                        {am.user_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" noWrap>
                        {am.user_email}
                      </Typography>
                      <Chip
                        size="small"
                        label={`Score: ${am.performance_score}`}
                        color={index === 0 ? 'primary' : 'default'}
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  {/* Metrics Grid */}
                  <Grid container spacing={1.5}>
                    <Grid item xs={6}>
                      <MetricDisplay
                        icon={PersonAddIcon}
                        label="Creators"
                        value={am.creators_signed}
                        color="success"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <MetricDisplay
                        icon={PersonAddIcon}
                        label="Clients"
                        value={am.clients_created}
                        color="info"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <MetricDisplay
                        icon={CampaignIcon}
                        label="Proposals"
                        value={am.proposals_created}
                        color="warning"
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <MetricDisplay
                        icon={CampaignIcon}
                        label="Launched"
                        value={am.campaigns_launched}
                        color="primary"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <MetricDisplay
                        icon={MoneyIcon}
                        label="Paid Campaigns Value"
                        value={am.total_campaign_value}
                        color="success"
                      />
                    </Grid>
                  </Grid>

                  {/* Performance Bar */}
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Performance
                      </Typography>
                      <Typography variant="caption" fontWeight={600}>
                        {Math.min(100, Math.round((am.performance_score / Math.max(...metrics.map(m => m.performance_score))) * 100))}%
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, (am.performance_score / Math.max(...metrics.map(m => m.performance_score))) * 100)}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      }}
                    />
                  </Box>
                </CardContent>
              </GlassCard>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={handleCloseDetails}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            backdropFilter: 'blur(20px)',
            backgroundColor: alpha(theme.palette.background.paper, 0.95),
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {selectedAM && (
                <>
                  <Avatar src={selectedAM.user_photo} sx={{ width: 48, height: 48 }}>
                    {selectedAM.user_name.charAt(0)}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" fontWeight={700}>
                      {selectedAM.user_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Detailed Performance Breakdown
                    </Typography>
                  </Box>
                </>
              )}
            </Box>
            <IconButton onClick={handleCloseDetails} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          {detailsLoading ? (
            <Box sx={{ py: 4 }}>
              <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 2 }} />
            </Box>
          ) : detailsData ? (
            <>
              <Tabs value={detailsTab} onChange={(_, v) => setDetailsTab(v)} sx={{ mb: 3 }}>
                <Tab label={`Creators (${detailsData.creators?.length || 0})`} />
                <Tab label={`Clients (${detailsData.clients?.length || 0})`} />
                <Tab label={`Companies (${detailsData.companies?.length || 0})`} />
                <Tab label={`Campaigns (${detailsData.campaigns?.length || 0})`} />
              </Tabs>

              {/* Creators Tab */}
              {detailsTab === 0 && (
                <List sx={{ p: 0 }}>
                  {detailsData.creators?.map((creator) => {
                    const statusConfig = getCreatorStatusConfig(creator.status);
                    return (
                      <ClickableListItem 
                        key={creator.creator}
                        onClick={() => handleNavigate(`/profile/${creator.creator}`)}
                      >
                        <ListItemAvatar>
                          <Avatar src={creator.pfphref}>
                            {creator.display_name?.charAt(0)}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body1" fontWeight={600}>
                                {creator.display_name || creator.creator}
                              </Typography>
                              {creator.status && <CreatorStatusChip status={creator.status} />}
                            </Box>
                          }
                          secondary={`Added: ${format(new Date(creator.date_added), 'MMM dd, yyyy')}`}
                        />
                        <ChevronRightIcon sx={{ color: 'text.secondary' }} />
                      </ClickableListItem>
                    );
                  })}
                  {(!detailsData.creators || detailsData.creators.length === 0) && (
                    <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                      No creators signed up in this period
                    </Typography>
                  )}
                </List>
              )}

              {/* Clients Tab */}
              {detailsTab === 1 && (
                <List sx={{ p: 0 }}>
                  {detailsData.clients?.map((client) => (
                    <ClickableListItem 
                      key={client.client_id}
                      onClick={() => handleNavigate(`/client/${client.client_id}`)}
                    >
                      <ListItemAvatar>
                        <Avatar src={client.profilephoto}>
                          {client.full_name?.charAt(0)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="body1" fontWeight={600}>
                            {client.full_name}
                          </Typography>
                        }
                        secondary={`${client.company} • Created: ${format(new Date(client.date_created), 'MMM dd, yyyy')}`}
                      />
                      <ChevronRightIcon sx={{ color: 'text.secondary' }} />
                    </ClickableListItem>
                  ))}
                  {(!detailsData.clients || detailsData.clients.length === 0) && (
                    <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                      No clients created in this period
                    </Typography>
                  )}
                </List>
              )}

              {/* Companies Tab */}
              {detailsTab === 2 && (
                <List sx={{ p: 0 }}>
                  {detailsData.companies?.map((company) => (
                    <ClickableListItem 
                      key={company.id}
                     /* onClick={() => handleNavigate(`/company/${company.id}`)} */
                    >
                      <ListItemAvatar>
                        <Avatar src={company.phphref}>
                          <BusinessIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="body1" fontWeight={600}>
                            {company.name}
                          </Typography>
                        }
                        secondary={`Created: ${format(new Date(company.account_created), 'MMM dd, yyyy')}`}
                      />
                      <ChevronRightIcon sx={{ color: 'text.secondary' }} />
                    </ClickableListItem>
                  ))}
                  {(!detailsData.companies || detailsData.companies.length === 0) && (
                    <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                      No companies assigned in this period
                    </Typography>
                  )}
                </List>
              )}

              {/* Campaigns Tab */}
              {detailsTab === 3 && (
                <List sx={{ p: 0 }}>
                  {detailsData.campaigns?.map((campaign) => {
                    const statusConfig = CAMPAIGN_STATUS_CONFIG[campaign.campaign_status] || CAMPAIGN_STATUS_CONFIG['Draft'];
                    return (
                      <ClickableListItem 
                        key={campaign.id}
                        onClick={() => handleNavigate(`/campaigns/${campaign.id}`)}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography variant="body1" fontWeight={600}>
                                {campaign.name}
                              </Typography>
                              <CampaignStatusChip status={campaign.campaign_status} />
                            </Box>
                          }
                          secondary={
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                              <Typography variant="body2" color="text.secondary">
                                <strong>{formatCurrency(campaign.campaign_sum)}</strong>
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Proposed: {format(new Date(campaign.proposal_date), 'MMM dd, yyyy')}
                                {campaign.paid_date && ` • Paid: ${format(new Date(campaign.paid_date), 'MMM dd, yyyy')}`}
                              </Typography>
                            </Box>
                          }
                        />
                        <ChevronRightIcon sx={{ color: 'text.secondary' }} />
                      </ClickableListItem>
                    );
                  })}
                  {(!detailsData.campaigns || detailsData.campaigns.length === 0) && (
                    <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                      No campaigns created in this period
                    </Typography>
                  )}
                </List>
              )}
            </>
          ) : null}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseDetails}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AMTracking;